import React from "react";
import { Anchor, Container, Group, Paper, Stack, Text } from "@mantine/core";
import { useGetSystemSettings } from "../api/systemSettings";
import { IconExternalLink } from "@tabler/icons-react";
import { IconEdit } from "@tabler/icons-react";
import { useSetActionButtons } from "../services/ActionButtonsContext";
import { useMemo } from "react";

function Row({ label, children }) {
  return (
    <Group justify="space-between" wrap="nowrap">
      <Text fw={500}>{label}</Text>
      <div>{children}</div>
    </Group>
  );
}

function LinkCell({ url, label }) {
  if (!url) {
    return <Text c="dimmed">-</Text>;
  }

  let host = url;
  try {
    host = new URL(url).hostname.replace(/^www\./, "");
  } catch {
    host = url;
  }

  return (
    <Anchor href={url} target="_blank" rel="noreferrer" title={url}>
      <Group gap={6} wrap="nowrap" align="center">
        <Text inherit>{label || host}</Text>
        <IconExternalLink size={16} />
      </Group>
    </Anchor>
  );
}

function SystemSettings() {
  const { data, isPending } = useGetSystemSettings();

  const actions = useMemo(
    () => [
      {
        label: "Edit",
        icon: <IconEdit />,
        navigate: "./system-settings/edit",
      },
    ],
    []
  );

  useSetActionButtons(actions);

  if (isPending) {
    return (
      <Container size={700}>
        <Paper withBorder shadow="md" p={16} mt={16} radius="md">
          <Text>Loading system settings…</Text>
        </Paper>
      </Container>
    );
  }

  if (!data) {
    return (
      <Container size={700}>
        <Paper withBorder shadow="md" p={16} mt={16} radius="md">
          <Text c="red">Unable to load system settings.</Text>
        </Paper>
      </Container>
    );
  }

  const yesNo = (v) => (v ? "Yes" : "No");

  return (
    <Container size={700}>
      <Paper withBorder shadow="md" p={16} mt={16} radius="md">
        <Stack gap="md">
          <Row label="Minimum iOS Version">
            <Text>{data.minIosVersion}</Text>
          </Row>

          <Row label="Minimum Android Version">
            <Text>{data.minAndroidVersion}</Text>
          </Row>

          <Row label="Force Expo Updates iOS">
            <Text>
              {yesNo(
                data.forceExpoUpdatesIos === true ||
                  data.forceExpoUpdatesIos === "true"
              )}
            </Text>
          </Row>

          <Row label="Force Expo Updates Android">
            <Text>
              {yesNo(
                data.forceExpoUpdatesAndroid === true ||
                  data.forceExpoUpdatesAndroid === "true"
              )}
            </Text>
          </Row>

          <Row label="Use New Ticket System(Mobile)">
            <Text>
              {yesNo(
                data.useNewTicketSystem === true ||
                  data.useNewTicketSystem === "true"
              )}
            </Text>
          </Row>

          <Row label="Use New Mobile App">
            <Text>
              {yesNo(
                data.useNewMobileApp === true || data.useNewMobileApp === "true"
              )}
            </Text>
          </Row>

          <Row label="Set Up screen">
            <Text>
              {yesNo(
                data.enableSetupScreen === true ||
                  data.enableSetupScreen === "true"
              )}
            </Text>
          </Row>

          <Row label="iOS App Store URL">
            <LinkCell url={data.iosAppStoreUrl} label="Open in App Store" />
          </Row>

          <Row label="Android App Store URL">
            <LinkCell
              url={data.androidAppStoreUrl}
              label="Open on Google Play"
            />
          </Row>
        </Stack>
      </Paper>
    </Container>
  );
}

export default SystemSettings;
